import { CloseOutlined, MinusOutlined, RobotOutlined, SendOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Button, Input, Space, Spin, Typography, message as antMessage } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useSendMessage } from '../hooks/use-send-msg';
import dayjs from 'dayjs';
import styled from '@emotion/styled';

const { Text } = Typography;

const primaryColor = '#ae2630';
const secondaryColor = '#f24b4b';

export function FloatChatWindow({ isOpen, isMinimized, setIsMinimized, setUnreadCount, handleToggle }) {
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: 'สวัสดีครับ! ผมคือ AI Assistant ยินดีให้บริการครับ',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const { mutate: sendMessage, isLoading: isSending } = useSendMessage({
    onSuccess: (res) => {
      const messageText = res?.response || 'ได้รับข้อความแล้วครับ';

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: messageText,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);
      setIsTyping(false);

      const convId = res?.response?.conversation_id;
      if (convId) {
        setConversationId(convId);
      }

      if (!isOpen) {
        setUnreadCount((prev) => prev + 1);
      }
    },
    onError: (error) => {
      console.error('Error sending message:', error);
      antMessage.error('ไม่สามารถส่งข้อความได้ กรุณาลองใหม่อีกครั้ง');
      setIsTyping(false);

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: 'ขออภัยครับ ขณะนี้ระบบมีปัญหา กรุณาลองใหม่อีกครั้งในภายหลัง',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);
    },
  });

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isSending) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: inputValue,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageText = inputValue;
    setInputValue('');
    setIsTyping(true);

    sendMessage({
      message: messageText,
      conversationId,
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <ChatWindow isMinimized={isMinimized}>
      {/* Header */}
      <ChatHeader primaryColor={primaryColor} secondaryColor={secondaryColor}>
        <Space>
          <Avatar icon={<RobotOutlined />} style={{ backgroundColor: primaryColor, boxShadow: '0 0 5px #ff3c3c' }} />
          <div>
            <Text strong style={{ color: 'white', display: 'block' }}>
              AI Assistant
            </Text>

            <Space size={5} align="center">
              <div style={{ backgroundColor: '#52c41a', width: 10, height: 10, borderRadius: '50%' }} />
              <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>Online</Text>
            </Space>
          </div>
        </Space>
        <Space>
          <Button
            type="text"
            size="small"
            icon={<MinusOutlined />}
            onClick={handleMinimize}
            style={{ color: 'white' }}
          />
          <Button type="text" size="small" icon={<CloseOutlined />} onClick={handleToggle} style={{ color: 'white' }} />
        </Space>
      </ChatHeader>

      {/* Messages Container */}
      {!isMinimized && (
        <>
          <MessagesContainer>
            {messages.map((message) => (
              <MessageWrapper key={message.id} type={message.type}>
                {message.type === 'bot' && (
                  <Avatar
                    size="small"
                    icon={<RobotOutlined />}
                    style={{ backgroundColor: primaryColor, boxShadow: '0 0 5px #ff3c3c', marginRight: '8px' }}
                  />
                )}
                <MessageBubble type={message.type} primaryColor={primaryColor} secondaryColor={secondaryColor}>
                  <Text style={{ color: '#fff', whiteSpace: 'pre-wrap', lineHeight: '1.5em' }}>{message.text}</Text>
                  <Text
                    style={{
                      fontSize: '10px',
                      color: message.type === 'user' ? 'rgba(255,255,255,0.7)' : '#999',
                      display: 'block',
                      marginTop: '4px',
                    }}
                  >
                    {dayjs(message.timestamp).format('HH:mm')}
                  </Text>
                </MessageBubble>
                {message.type === 'user' && (
                  <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#000', marginLeft: '8px' }} />
                )}
              </MessageWrapper>
            ))}

            {isTyping && (
              <MessageWrapper type="bot">
                <Avatar
                  size="small"
                  icon={<RobotOutlined />}
                  style={{ backgroundColor: primaryColor, marginRight: '8px' }}
                />
                <TypingIndicator>
                  <Spin size="small" />
                  <Text style={{ marginLeft: '8px', color: '#999' }}>กำลังพิมพ์...</Text>
                </TypingIndicator>
              </MessageWrapper>
            )}
            <div ref={messagesEndRef} />
          </MessagesContainer>

          {/* Input Area */}
          <InputAreas>
            <Input.TextArea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyUp={handleKeyPress}
              placeholder="พิมพ์ข้อความ..."
              autoSize={{ minRows: 1, maxRows: 3 }}
              style={{
                resize: 'none',
                borderRadius: '20px',
                padding: '8px 16px',
                borderColor: '#5a6772',
                color: '#E0E0E0',
                backgroundColor: '#4d5a67',
              }}
              disabled={isSending}
            />
            <Button
              type="primary"
              shape="circle"
              icon={<SendOutlined />}
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isSending}
              loading={isSending}
              style={{ marginLeft: '8px' }}
            />
          </InputAreas>
        </>
      )}
    </ChatWindow>
  );
}

const ChatWindow = styled.div(
  ({ isMinimized }) => `position: fixed;
  bottom: 24px;
  right: 24px;
  width: 380px;
  height: ${isMinimized ? '60px' : '600px'};
  background: #38424b;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  transition: all 0.3s ease;
  overflow: hidden;

  @media (max-width: 768px) {
    width: calc(100vw - 32px);
    height: ${isMinimized ? '60px' : 'calc(100vh - 100px)'};
    bottom: 16px;
    right: 16px;
  }`
);

const ChatHeader = styled.div(
  ({ primaryColor, secondaryColor }) => `
  background: linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%);
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 16px 16px 0 0;
`
);

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #38424b;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #bfbfbf;
  }
`;

const MessageWrapper = styled.div(
  ({ type }) => `
  display: flex;
  align-items: flex-end;
  margin-bottom: 12px;
  justify-content: ${type === 'user' ? 'flex-end' : 'flex-start'};
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`
);

const MessageBubble = styled.div(
  ({ type, primaryColor, secondaryColor }) => `
  max-width: 70%;
  padding: 12px 16px;
  border-radius: ${type === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px'};
  background: ${type === 'user' ? `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` : '#4d5a67'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  word-wrap: break-word;
`
);

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #4d5a67;
  border-radius: 16px 16px 16px 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const InputAreas = styled.div`
  padding: 16px;
  background: #434f5a;
  border-top: 1px solid #5a6772;
  display: flex;
  align-items: flex-end;
  border-radius: 0 0 16px 16px;
`;
